import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { useRouter } from "src/services/Router";
import { positiveId, isRecord } from "./trafficAssignmentsData";
import { ruleSummaryText, type RuleRecord } from "./ruleBlocksData";

const Party = ({ type, value, clientId, projectId, onNavigate }: { type: "client" | "merchant"; value: unknown; clientId: number | null; projectId?: number; onNavigate?: () => void }) => {
	useLocale();
	const { t } = useTranslation();
	const router = useRouter();
	const id = positiveId(value);
	const [result, setResult] = useState<{ name?: string; domain?: string } | null>(null);
	useEffect(() => {
		if (!id || !projectId) return;
		const controller = new AbortController();
		GD.REQ_API.request({
			method: type === "client" ? "clients/get" : "clients/group/get",
			data: type === "client" ? { id } : { group_id: id, ...(clientId ? { client_id: clientId } : {}) },
			expectedProjectId: projectId, signal: controller.signal, headers: { ignoreBusy: "true" },
		}).then(response => {
			if (controller.signal.aborted) return;
			const data = !response?.error && isRecord(response?.data) ? response.data : null;
			setResult(data && positiveId(data.id) === id ? {
				name: typeof data.name === "string" && data.name.trim() ? data.name : undefined,
				domain: type === "merchant" && typeof data.domain === "string" ? data.domain : undefined,
			} : {});
		}).catch(() => { if (!controller.signal.aborted) setResult({}); });
		return () => controller.abort();
	}, [id, clientId, projectId, type]);
	const content = <>
		<strong>{result?.name || result?.domain || (!id ? t("common.notSet") : projectId && result === null ? t("common.loading") : t("administration.partyUnavailable"))}</strong>
		{result?.name && result.domain && result.domain !== result.name && <div>{result.domain}</div>}
		<div>{type === "client" ? msg("id_3843971d") : msg("mid")}: {ruleSummaryText(value)}</div>
	</>;
	const href = projectId && id && (type === "client" || clientId)
		? type === "client" ? `/clients/${id}` : `/clients/${clientId}/merchant/${id}`
		: undefined;
	return <div><dt>{t(`nav.${type}`)}</dt><dd>{href ? <a className="party-link" href={href} onClick={event => {
		if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
		event.preventDefault();
		onNavigate?.();
		if (type === "client") router.navigate("client", { cid: String(id) });
		else router.navigate("merchant", { cid: String(clientId), mid: String(id) });
	}}>{content}</a> : content}</dd></div>;
};

export const RuleBlockParties = ({ record, projectId, onNavigate }: { record: RuleRecord; projectId?: number; onNavigate?: () => void }) => {
	useLocale();
	const context = !record.project_id || positiveId(record.project_id) === projectId ? projectId : undefined;
	return <dl className="parties">{(["client", "merchant"] as const).map(type => <Party
		key={`${context}:${type}:${String(record.client_id)}:${String(record.mid)}`}
		type={type} value={type === "client" ? record.client_id : record.mid} clientId={positiveId(record.client_id)} projectId={context}
		onNavigate={onNavigate}
	/>)}</dl>;
};
